import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Table } from 'primeng/table';
import { forkJoin, of, Subject, Subscription, throwError } from 'rxjs';
import { format, utcToZonedTime } from 'date-fns-tz';
import { ConfirmationService, LazyLoadEvent, PrimeNGConfig } from 'primeng/api';
import { Router } from '@angular/router';
import { ITableConfig } from 'src/app/core/interfaces/tableConfig.interface';
import { IGeneralText } from 'src/app/core/interfaces/generalText.interface';
import { RequestService } from '../../core/service/request.service';
import { TableConfigService } from 'src/app/core/service/table-config.service';
import { TextService } from 'src/app/core/service/text.service';
import { BiaUtils } from 'src/@utility/bia.utils';
import { Store } from '@ngrx/store';
import * as fromUser from 'src/app/root-store/user/user.selectors';
import * as UserAction from 'src/app/root-store/user/user.actions';
import { catchError, map } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import * as uuid from 'uuid';
import { StorageService } from 'src/app/core/service/storage.service';
import CONSTANTS from 'src/@utility/constants';
import { Transformation } from 'src/@utility/model-transformation';
import { KeycloakService } from 'keycloak-angular';
import { FormGroup } from '@angular/forms';
import { CommonMessageService } from 'src/app/services/common-message.service';
import { Loading } from '../base-loader/loading';

interface FilterArgs {
  value: any;
  field: any;
  matchMode: any;
}
@Component({
  selector: 'app-base-table',
  templateUrl: './base-table.component.html',
  styleUrls: ['./base-table.component.scss'],
})
export class BaseTableComponent implements OnInit, OnDestroy {
  @ViewChild('dt') table: Table;
  @Input() enablePaging = true;
  @Input() pagingType: 'server-side' | 'client-side' = 'server-side';
  @Input() data: any[];
  @Input() headerData?: any;
  @Input() dataCallback: (options, setDataFn, tableConfig) => void;
  @Input() cols: any[] = [];
  @Input() customAction: any;
  @Input() highlightRows = [];
  @Input() tableId: string;
  @Input() query: any = {};
  @Input() postData: any = {};
  @Input() filteredDataExport = [];
  @Input() detectRefresh?: Subject<boolean>;
  @Input() transformModelFn: {
    forward: (model, config?: any) => any;
    backward: (model, config?: any) => any;
  };
  @Input() dataSelectionDisabaled = false;
  @Input() readonly = false;
  @Input() transformDataFn: (data, config?: any) => any;
  @Input() modifyConfigFn: (config: any) => any;
  @Input('selectedData') dataSelected = [];
  @Input() dataSelectionKey = 'id';

  @Output() onRowSelect = new EventEmitter();
  @Output() onRowUnselect = new EventEmitter();
  @Output() onFormInit = new EventEmitter<FormGroup>();
  @Output() onFormDataInput = new EventEmitter();
  @Output() onTableColumnsChange = new EventEmitter();
  @Output() onEdit = new EventEmitter();
  @Output() onAdd = new EventEmitter();
  @Output() onView = new EventEmitter();
  @Output() onDelete = new EventEmitter();
  @Output() onSoftDelete = new EventEmitter();
  @Output() onInfoClick = new EventEmitter();
  @Output() onCustomAction = new EventEmitter();
  @Output() onDataSelectionChanged = new EventEmitter();

  page = 0;
  totalRows: any = 0;
  selectedData: any[];
  isNew = false;
  _tempOriginal: any;
  formFields: any[];
  tableCols: any[];
  exportColumns: any[];
  visible: boolean;
  isXlsUpload = false;
  editRecord: any;
  submitted: boolean;
  globalFilterFields = [];
  filteredData = [];
  multiSortMeta: any[];
  servicesToBeReloaded = false;
  allSelected = false;
  newRecord = false;
  searchFilter: { [key: string]: any } = {};
  uniqueId: string;
  subscriptions: Subscription[] = [];
  generalTexts: IGeneralText = {
    currentPageReport: { labelId: 'L_BT_SEARCH_TEXT_PAGE' },
    searchBy: { labelId: 'L_BT_SEARCH_BY' },
    newButton: { labelId: 'L_NEW' },
    newButtonHelpText: { labelId: 'HT_ADD_RECORD' },
    action: { labelId: 'L_ACTION' },
    noDataText: { labelId: 'L_NODATA' },
    confirm: { labelId: 'L_CONFIRMATION' },
    recordDelMsg: { labelId: 'L_CONFIRM_DELETE' },
    successfull: { labelId: 'L_SUCCESSFUL' },
    success: { labelId: 'L_SUCCESS' },
    recordDeleted: { labelId: 'L_RECORD_DELETED' },
    yes: { labelId: 'L_YES' },
    no: { labelId: 'L_NO' },
    uploadXls: { labelId: 'L_UPLOAD_XLS' },
    downloadTemplate: { labelId: 'L_DOWNLOAD_TEMPLATE' },
    downloadXLs: { labelId: 'L_DOWNLOAD_XLS' },
    downloadPdf: { labelId: 'L_DOWNLOAD_PDF' },
    search: { labelId: 'PH_SEARCH' },
    exportFile: { labelId: 'L_EXPORT_DATA' },
  };

  private _tableConfig: ITableConfig = {
    size: 10,
    method: 'GET',
    url: '',
    title: '',
    loading: true,
    responsive: true,
    export: false,
    pagging: true,
    globalSearch: false,
    editButton: false,
    viewButton: false,
    updateUrl: '',
    softDelete: false,
    deleteButton: false,
    deleteUrl: '',
    newRecordButton: false,
    selectedDeleteButton: false,
    dataSelection: false,
    maximizable: false,
    editCallBack: false,
    viewCallBack: false,
    newCallBack: false,
    deleteCallBack: false,
    transformModel: false,
    transformData: false,
    wrapperClass: 'card',
    actionColumnHidden: false,
    modifyConfig: false,
    query: {},
    excel: {},
    editBtnToView: false,
    lazy: true,
    objToStr: [],
  };

  @Input()
  get tableConfig(): any {
    return this._tableConfig;
  }
  set tableConfig(value: any) {
    if (Object.keys(value).length) {
      if (value.globalSearch) {
        this._tableConfig.globalSearch = value.globalSearch;
      }
      if (value.pagging === false) {
        this._tableConfig.pagging = value.pagging;
      }
      if (value.size) {
        this._tableConfig.size = value.size;
      }
      if (value.method) {
        this._tableConfig.method = value.method;
      }
      if (value.url) {
        this._tableConfig.url = value.url;
      }
      if (value.title) {
        this._tableConfig.title = value.title;
      }
      if (value.loading) {
        this._tableConfig.loading = value.loading;
      }
      if (value.export) {
        this._tableConfig.export = value.export;
      }
      if (typeof value.responsive !== 'undefined') {
        this._tableConfig.responsive = value.responsive;
      }
      if (value.editButton) {
        this._tableConfig.editButton = value.editButton;
      }
      if (value.viewButton) {
        this._tableConfig.viewButton = value.viewButton;
      }
      if (value.softDelete) {
        this._tableConfig.softDelete = value.softDelete;
      }
      if (value.deleteButton) {
        this._tableConfig.deleteButton = value.deleteButton;
      }

      if (value.selectedDeleteButton) {
        this._tableConfig.selectedDeleteButton = value.selectedDeleteButton;
      }

      if (value.dataSelection) {
        this._tableConfig.dataSelection = value.dataSelection;
      }

      if (value.newRecordButton) {
        this._tableConfig.newRecordButton = value.newRecordButton;
      }

      if (value.maximizable) {
        this._tableConfig.maximizable = value.maximizable;
      }

      if (value.addUrl) {
        this._tableConfig.addUrl = value.addUrl;
      }

      if (value.editUrl) {
        this._tableConfig.editUrl = value.editUrl;
      }

      if (value.deleteUrl) {
        this._tableConfig.deleteUrl = value.deleteUrl;
      }

      if (value.updateUrl) {
        this._tableConfig.updateUrl = value.updateUrl;
      }

      if (value.editCallBack) {
        this._tableConfig.editCallBack = value.editCallBack;
      }
      if (value.newCallBack) {
        this._tableConfig.newCallBack = value.newCallBack;
      }
      if (value.viewCallBack) {
        this._tableConfig.viewCallBack = value.viewCallBack;
      }
      if (value.deleteCallBack) {
        this._tableConfig.deleteCallBack = value.deleteCallBack;
      }
      if (value.transformModel) {
        this._tableConfig.transformModel = value.transformModel;
      }

      if (value.transformData) {
        this._tableConfig.transformData = value.transformData;
      }

      if (value.columnCustomizer) {
        this._tableConfig.columnCustomizer = value.columnCustomizer;
      }

      if (value.wrapperClass !== undefined) {
        this._tableConfig.wrapperClass = value.wrapperClass;
      }

      if (value.actionColumnHidden !== undefined) {
        this._tableConfig.actionColumnHidden = value.actionColumnHidden;
      }
      if (value.hasCustomAction !== undefined) {
        this._tableConfig.hasCustomAction = value.hasCustomAction;
      }
      if (value.hasHeaderCustomAction !== undefined) {
        this._tableConfig.hasHeaderCustomAction = value.hasHeaderCustomAction;
      }
      if (value.modifyConfig !== undefined) {
        this._tableConfig.modifyConfig = value.modifyConfig;
      }

      if (value.query !== undefined) {
        this._tableConfig.query = { ...this.query, ...value.query };
      } else if (this.query) {
        this._tableConfig.query = this.query;
      }
      if (value.excel !== undefined) {
        this._tableConfig.excel = value.excel;
      }
      if (value.editBtnToView !== undefined) {
        this._tableConfig.editBtnToView = value.editBtnToView;
      }
      if (value.lazy !== undefined) {
        this._tableConfig.lazy = value.lazy;
      }
      if (value.permissions !== undefined) {
        this._tableConfig.permissions = value.permissions;
      }
      if (value.objToStr !== undefined) {
        this._tableConfig.objToStr = value.objToStr;
      }
    }
  }

  constructor(
    private primengConfig: PrimeNGConfig,
    private restApi: RequestService,
    private commonMessageService: CommonMessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private tableConfigService: TableConfigService,
    private textService: TextService,
    private ref: ChangeDetectorRef,
    private store: Store,
    private http: HttpClient,
    private storage: StorageService,
    private keycloakService: KeycloakService,
    private loading: Loading
  ) {
    this.tableConfigModifier = this.tableConfigModifier.bind(this);
    this.hasPermission = this.hasPermission.bind(this);
    const appKeyValue = this.storage.getItem(CONSTANTS.appKeyValuesStoreKey);
    this._tableConfig.size = +appKeyValue.TOTAL_RECORDS_PER_TABLE || 10;
  }

  tableInit() {
    this.refreshTable();
    this.exportColumns = this.cols.map((col) => ({
      title: col.label,
      dataKey: col.field,
    }));

    this.cols.forEach((d) => {
      if (d.globalSearch) {
        this.globalFilterFields?.push(d.filterKey || d.field);
      }
    });

    this.formFields = this.cols.filter(this.shouldHaveControlInForm);
    this.tableCols = this.cols.filter(this.shouldHaveColInTable);

    this.primengConfig.ripple = true;

    this.processSelectElementWithSourceUrl(() => {
      if (this.cols) {
        this.titleNeedsToBeTranslated();
        this.textService
          .convert(
            {
              cols: this.cols,
              generalTexts: this.generalTexts,
            },
            {
              convertKeyValue: {
                labelId: 'label',
                placeholderId: 'placeholder',
                messageId: 'message',
              },
            }
          )
          .subscribe((converted) => {
            this.cols = converted.cols;
            this.generalTexts = converted.generalTexts;
          });
        if (this.hasPermission('view')) {
          this.getDataFromAPI(this.generateCriteria());
        } else {
          this.data = [];
          this._tableConfig.loading = false;
        }
      }
    });
  }

  private refreshTable() {
    this.detectRefresh?.subscribe((refresh) => {
      if (refresh) {
        this.getDataFromAPI();
      }
    });
  }

  private generateCriteria(): any {
    if (!this.cols) {
      return {};
    }
    return this.cols
      .filter((col) => col.defaultOption && col.defaultOption.value)
      .reduce((result, col) => {
        result[(col.filterKey ?? col.field) + '.equals'] =
          col.defaultOption?.value;
        return result;
      }, {});
  }

  ngOnInit(): void {
    this.uniqueId = uuid.v4();
    if (!this.tableId) {
      return this.tableInit();
    }

    this.tableConfigService.load(this.tableId).subscribe((tableConfigResp) => {
      if (tableConfigResp) {
        const tableConfig =
          this.modifyConfigFn && tableConfigResp.tableConfig.modifyConfig
            ? this.modifyConfigFn(tableConfigResp)
            : tableConfigResp;
        this.cols = tableConfig.tableCols;
        this.tableConfig = tableConfig.tableConfig;

        this.tableInit();
      }
    });
  }

  titleNeedsToBeTranslated() {
    if (this.tableConfig.title) {
      this.generalTexts['titleText'] = { labelId: this.tableConfig.title };
    }
  }

  processSelectElementWithSourceUrl(done) {
    this.servicesToBeReloaded = false;
    const selectFieldRequests = this.formFields
      .map((field, idx) => ({ ...field, idx }))
      .filter(
        (field) =>
          (field.type === 'select' || field.type === 'multiselect') &&
          typeof field.sourceUrl !== 'undefined'
      )
      .reduce((allProcess, nextField) => {
        const service = this.restApi
          .get(nextField.sourceUrl)
          .pipe(
            map((resp) =>
              nextField.sourceTransformFn
                ? resp.body.map(Transformation[nextField.sourceTransformFn])
                : resp.body
            )
          );
        if (nextField.refreshSource) {
          this.servicesToBeReloaded = true;
        }
        allProcess[nextField.idx] = service;
        return allProcess;
      }, {});

    if (!Object.getOwnPropertyNames(selectFieldRequests).length) {
      return done && done();
    }
    forkJoin(selectFieldRequests).subscribe((result) => {
      Object.keys(result).forEach((key) => {
        const field = this.formFields[+key];
        const transformedValue = field.isTimeInterval
          ? BiaUtils.processTimeString(result[key])
          : result[key];
        if (field.sourceTranslateFn) {
          this.textService
            .convert(transformedValue)
            .subscribe((convertedValue) => {
              field.source = convertedValue;
            });
        } else {
          field.source = transformedValue;
        }
      });
      done();
    });
  }

  shouldHaveColInTable(col): boolean {
    return !col.columnHidden;
  }

  shouldHaveControlInForm(col): boolean {
    return !col.isDerived;
  }

  shouldShowHeaderFilterRow(cols): boolean {
    return cols?.reduce((all, cur) => all || cur.filter, false);
  }

  getDataFromAPI(filterCriteria = {}, rows = 0): void {
    // Adjust page size if changed
    if (rows && this._tableConfig.size !== rows) {
      const appKeyValues = this.storage.getItem(CONSTANTS.appKeyValuesStoreKey);
      this._tableConfig.size = rows;
      this.storage.setItem(CONSTANTS.appKeyValuesStoreKey, {
        ...appKeyValues,
        TOTAL_RECORDS_PER_TABLE: rows,
      });
    }

    let serverSideObj: Partial<{ page: number; size: number }> = {};

    if (this.pagingType === 'server-side') {
      serverSideObj = {
        page: this.page,
        size: this._tableConfig.size,
      };
    }

    const options = Object.assign(
      serverSideObj,
      this._tableConfig.query,
      filterCriteria
    );

    if (this.multiSortMeta && this.multiSortMeta.length) {
      this.multiSortMeta.forEach((d) => {
        options['sort'] = `${d.field},${d.order === 1 ? 'desc' : 'asc'}`;
      });
    }

    if (this._tableConfig.softDelete) {
      options['isEnabled.equals'] = true;
    }

    if (this.dataCallback) {
      this.dataCallback(
        options,
        (data, total) => this.setData(data, total),
        this.tableConfigModifier
      );
      return;
    }

    const subscription = this.restApi
      .request(this._tableConfig.method, this._tableConfig.url || '', options)
      .pipe(
        map((response) => {
          let data = [];
          let total = 0;
          if (response && response.body) {
            data = response.body;
            total = response.headers.get('x-total-count') || 0;
          }
          return { data, total };
        }),
        catchError(() => of({ data: [], total: 0 }))
      )
      .subscribe(({ data, total }) => {
        this.setData(data, total);
      });
    this.subscriptions.push(subscription);
  }

  tableConfigModifier(cb) {
    this._tableConfig = cb(this._tableConfig);
  }

  setData(data, total) {
    this.totalRows = total;
    this.data =
      this._tableConfig.transformData && this.transformDataFn
        ? this.transformDataFn(data, this.formFields)
        : data;

    this.handleTableData();
  }

  private handleTableData() {
    this.transformTimeData(this.data);

    setTimeout(() => {
      if (this._tableConfig.dataSelection && this.dataSelected.length) {
        this.dataSelected = this.dataSelected.map((dts) =>
          this.data.find(
            (dt) =>
              dt['' + this.dataSelectionKey] === dts['' + this.dataSelectionKey]
          )
        );
      }
    }, 0);
    this._tableConfig.loading = false;
    this.allSelected =
      this.dataSelected.length && this.dataSelected.length === this.data.length;

    const asyncCols = this.tableCols?.filter((col) => col.asyncType);
    if (asyncCols && asyncCols.length) {
      const asyncGrouped = asyncCols.reduce((all, curCol) => {
        if (!all[curCol.asyncType]) {
          all[curCol.asyncType] = [];
        }
        all[curCol.asyncType].push(curCol);
        return all;
      }, {});

      this.onTableColumnsChange.emit(this.tableCols.map((col) => col.label));

      Object.keys(asyncGrouped).forEach((key) => {
        switch (key) {
          case 'user':
            this.dispatchUserActions(asyncGrouped[key]);
        }
      });
    }
  }

  transformTimeData(data) {
    const timeFields = this.cols?.filter((field) => field.isTimeInterval);

    if (timeFields && timeFields.length) {
      this.data = data.map((dt) => {
        timeFields.forEach((tfield) => {
          const fieldValue = dt[tfield.field];
          dt[tfield.field] =
            fieldValue && fieldValue.id && tfield.source
              ? tfield.source.find((src) => src.id === fieldValue.id)
              : fieldValue;
        });
        return dt;
      });
    }
  }

  loadData(event: LazyLoadEvent): void {
    if (!this.hasPermission('view')) {
      return;
    }

    this._tableConfig.loading = true;
    if (event.multiSortMeta && event.multiSortMeta.length) {
      this.multiSortMeta = event.multiSortMeta;
    }
    this.page = event.first / event.rows;
    const { filters } = event;
    const filterCriteria = Object.getOwnPropertyNames(filters).reduce(
      (all, cur) => {
        if (cur !== 'global') {
          const obj = filters[cur];
          all[`${cur}.${obj.matchMode}`] = obj.value;
        }
        return all;
      },
      {}
    );
    setTimeout(() => {
      this.getDataFromAPI(filterCriteria, event.rows);
    }, 1000);
  }

  exportPdf(): void {
    import('jspdf').then((jsPDF) => {
      import('jspdf-autotable').then(() => {
        const doc: any = new jsPDF.default();
        doc.autoTable(this.pdfExportColumn, this.getFilterData);
        const fileName = this.generalTexts.exportFile?.label;
        doc.save(`${fileName}.pdf`);
      });
    });
  }

  handleGlobalSearch(ev: any) {
    if (this.tableCols && this.tableCols.length > 0) {
      if (this._tableConfig.globalSearch) {
        this.table.filterGlobal(ev.target.value, 'contains');
      } else {
        const argsList: FilterArgs[] = [];
        this.tableCols.forEach((col: any) => {
          let filterValue = ev.target.value || '';
          let filterType = col.filterType;
          if (col.boolean) {
            if (filterValue === 'true') {
              filterValue = true;
            } else if (filterValue === 'false') {
              filterValue = false;
            } else {
              return;
            }
            filterType = 'equals';
          }
          const filterArgs: FilterArgs = {
            value: filterValue,
            field: col.filterKey || col.field,
            matchMode: filterType || 'contains',
          };
          argsList.push(filterArgs);
        });
        const booleanList = argsList.filter((s) => s.matchMode === 'equals');
        this.table.filters = {};
        const filterFunction = (filterArgs) => {
          this.table?.filter(
            filterArgs.value,
            filterArgs.field,
            filterArgs.matchMode
          );
        };
        const filterList = booleanList.length > 0 ? booleanList : argsList;
        filterList.forEach(filterFunction);
      }
    }
  }

  handleInputSearch(ev: any, col: any) {
    this.searchFilter[col.field] = ev.target.value || '';
    this.table?.filter(
      ev.target.value,
      col?.filterKey || col?.field,
      'contains'
    );
  }

  onSelectChange($event: any, col: any): void {
    this.table?.filter($event?.value, col?.filterKey || col?.field, 'equals');
  }

  onMultiSelectChange($event: any, col: any): void {
    this.table?.filter($event?.value, col?.field, 'in');
  }

  onDateSelect(value: Date, col: any): void {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (value && col.dateFuture) {
      const zonedDate = utcToZonedTime(value, timeZone);
      const modifiedDate = new Date(
        zonedDate.getFullYear(),
        zonedDate.getMonth(),
        zonedDate.getDate(),
        0,
        0,
        0,
        0
      );
      const isoString = format(modifiedDate, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
      this.table?.filter(
        isoString,
        col.filterKey || col.field,
        col.filterType || 'equals'
      );
    } else {
      const zonedDate = utcToZonedTime(value, timeZone);
      const modifiedDate = new Date(
        zonedDate.getFullYear(),
        zonedDate.getMonth(),
        zonedDate.getDate(),
        23,
        59,
        59,
        999
      );
      const isoString = format(modifiedDate, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
      this.table?.filter(
        isoString,
        col.filterKey || col.field,
        col.filterType || 'equals'
      );
    }
  }

  clearSearchFilter(col) {
    this.searchFilter[col.field] = '';
    this.table?.filter('', col.filterKey || col.field, 'contains');
  }

  get getFilterData() {
    return this.filteredData.length <= 0 ? this.data : this.filteredData;
  }

  get pdfExportColumn() {
    return this.tableCols.map((col, index) => ({
      title: col.label,
      dataKey: 'C_' + index,
    }));
  }

  get exportColumn() {
    return [this.tableCols.map((col) => col.label)];
  }

  onFilter(event) {
    this.filteredData = event.filteredValue;
  }

  exportExcel(): void {
    import('xlsx').then((XLSX) => {
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet([]);
      XLSX.utils.sheet_add_aoa(ws, this.exportColumn);
      XLSX.utils.sheet_add_json(
        ws,
        this.filteredDataExport.length <= 0
          ? this.getFilterData
          : this.filteredDataExport,
        {
          origin: 'A2',
          skipHeader: true,
        }
      );
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      const fileName = this.generalTexts.exportFile?.label;
      XLSX.writeFile(wb, `${fileName}.xlsx`);
    });
  }

  /** VIew, Edit And Delete */
  viewData(data: any) {
    this.isNew = false;
    return this.onView.emit({
      ...data,
      viewOnly: true,
      tableConfig: this._tableConfig,
    });
  }

  editData(model: any): void {
    let record = model;
    this.isNew = false;
    const { editUrl } = this._tableConfig;
    if (this._tableConfig.editCallBack) {
      return this.onEdit.emit(record);
    } else if (!editUrl) {
      this._tempOriginal = { ...record };
      record = this._tableConfig.transformModel
        ? this.transformModelFn.backward(record, this.formFields)
        : this.processEditData(record);
      this.editRecord = { ...record, id: this._tempOriginal.id };
      this.visible = true;
      return;
    } else if (typeof editUrl === 'function') {
      editUrl(record);
      return;
    }
    this.router.navigate([editUrl, record.id]);
  }

  processEditData(record) {
    const newrecord = {};
    this.formFields.forEach((col) => {
      const optionValue = col.optionValue || 'value';
      switch (col.type) {
        case 'user-auto-select':
          newrecord[col.field] =
            record[col.field + '-async'] ?? record[col.field];
          break;
        case 'select':
          newrecord[col.field] =
            !col.expectedObject && record[col.field]
              ? { [optionValue]: record[col.field] }
              : record[col.field];
          break;

        case 'checkbox':
          newrecord[col.field] =
            col.single && !col.boolean
              ? [record[col.field]]
              : record[col.field];
          break;

        default:
          newrecord[col.field] = record[col.field];
      }
    });
    return newrecord;
  }

  deleteData(record: any): void {
    if (!this.hasPermission('delete')) {
      return;
    }

    this.confirmationService.confirm({
      message: this.generalTexts.recordDelMsg.label,
      header: this.generalTexts.confirm.label,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.generalTexts.yes.label,
      rejectLabel: this.generalTexts.no.label,
      accept: () => {
        let method = 'DELETE';
        let data = { id: record.id };
        const apiUrl =
          (this._tableConfig.deleteUrl || this._tableConfig.url) +
          '/' +
          (record.id || '');
        if (this._tableConfig.softDelete) {
          method = 'PUT';
          data = { ...record, isEnabled: false };

          if (this._tableConfig.deleteCallBack) {
            this.onSoftDelete.emit(data);
            return;
          }
        }

        if (this._tableConfig.objToStr.length) {
          this._tableConfig.objToStr.map((m: any) => {
            data[m.field] =
              typeof data[m.field] === 'object'
                ? data[m.field]?.[m.key]
                : data[m.field];
          });
        }

        this.restApi
          .request(method, apiUrl, data)
          .pipe(catchError(this.handleError.bind(this)))
          .subscribe(() => {
            this.onDelete.emit({ id: record.id });

            if (!this.dataCallback) {
              this.getDataFromAPI();
            } else {
              this.detectRefresh?.next(true);
            }

            if (this.servicesToBeReloaded) {
              this.processSelectElementWithSourceUrl(null);
            }

            this.ref.detectChanges();
            this.commonMessageService.success('delete');
          });
        this.confirmationService.close();
      },
      reject: () => {
        this.confirmationService.close();
      },
    });
  }

  handleError(error): any {
    let errorMessage: string;
    if (error.error && error.error.status < 500) {
      // client-side error
      errorMessage = `Error: ${error.error.message}`;
      const errorTextSub = this.textService
        .convert({ labelId: error.error.entityName })
        .subscribe((err) => {
          this.commonMessageService.error('add', err.label);
        });

      setTimeout(() => {
        errorTextSub?.unsubscribe();
      }, 2000);
    } else {
      // server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(errorMessage);
  }

  dispatchUserActions(cols) {
    const allUserIds = new Set<string>();
    this.data.forEach((record) => {
      cols.forEach((col) => {
        if (record[col.field]) {
          allUserIds.add(record[col.field]);
        }
      });
    });

    Array.from(allUserIds).forEach((userId) => {
      this.store.dispatch(UserAction.loadUser({ userId }));
    });
  }

  getContent(record, colInfo) {
    let selector: any;
    let payload: any;
    switch (colInfo.asyncType) {
      default:
        selector = fromUser.getUser;
        payload = { userId: record[colInfo.field] };
    }
    return this.store.select(selector, payload).pipe(
      map((user) => {
        if (user) {
          record[colInfo.field + '-async'] = user;
          return user[colInfo.optionKey];
        }
        return null;
      })
    );
  }

  getRowClass(row) {
    return Array.isArray(this.highlightRows) &&
      this.highlightRows.find((hRow) => row.id === hRow.id)
      ? 'p-highlight'
      : '';
  }

  /** New / selected Delete */
  openNew(): void {
    this.isNew = true;
    const { addUrl } = this._tableConfig;
    if (this._tableConfig.newCallBack) {
      return this.onAdd.emit();
    } else if (!addUrl) {
      this.newRecord = true;
      this.submitted = false;
      this.visible = true;
      return;
    }
    this.router.navigate([addUrl]);
  }

  selectAll(event) {
    const data = event.checked ? this.data : [];
    this.onRowSelect.emit({ event, data });
    this.onDataSelectionChanged.emit(data);
  }

  selectIndividual(event, data) {
    event?.checked
      ? this.onRowSelect.emit({ event, data })
      : this.onRowUnselect.emit({ event, data });

    this.allSelected = this.dataSelected.length === this.data.length;
    this.onDataSelectionChanged.emit(this.dataSelected);
  }

  getCellData(record, col) {
    let data = record[col.field];
    if (typeof data === 'boolean') {
      data =
        data === true
          ? this.generalTexts.yes.label
          : this.generalTexts.no.label;
    }
    if (typeof data === 'number') {
      data = data.toString();
    }
    if (col.sourceTranslateFn) {
      this.textService.convert(record).subscribe((res) => (record = res));
    }
    if (col.joinList) {
      const { joinKey = 'name', joinSeparator = ', ' } = col;

      return Array.isArray(data)
        ? data.map((dt) => dt[joinKey]).join(joinSeparator)
        : '';
    }

    function verifyBase64String() {
      return (
        !!col.base64 &&
        data !== null &&
        data.split('|').length &&
        /^data:([A-Za-z]+\/[A-Za-z]+);base64,([A-Za-z0-9+/]+={0,2})$/.test(
          data.split('|')[1]
        )
      );
    }

    if (verifyBase64String()) {
      return data.split('|')[0];
    }
    return (data && data[col.optionKey]) || data || '';
  }

  handleFormToggle(e) {
    this.visible = e.visible;
  }

  handleSave() {
    if (!this.dataCallback) {
      this.getDataFromAPI();
    } else {
      this.detectRefresh.next(true);
    }
    if (this.servicesToBeReloaded) {
      this.processSelectElementWithSourceUrl(null);
    }
  }
  infoClick(data) {
    this.onInfoClick.emit(data);
  }

  // upload excel
  uploadExcel() {
    this.isXlsUpload = true;
  }

  handleExcelToggle(e) {
    this.isXlsUpload = e.visible;
  }

  handleImport() {
    this.isXlsUpload = false;
    this.handleSave();
  }

  downloadExcelTemplate() {
    this.loading.show();
    const httpOptions: any = {
      headers: new HttpHeaders({
        Accept: 'application/octet-stream',
        'Cache-Control':
          'no-cache, no-store, must-revalidate, post- check=0, pre-check=0',
        Pragma: 'no-cache',
        Expires: '0',
      }),
      responseType: 'blob',
    };
    const apiUrl = this._tableConfig.excel['downloadUrl'];
    return this.http
      .get(environment.apiUrl + apiUrl, httpOptions)
      .subscribe((resp) => {
        const file = new Blob([resp], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const element = document.createElement('a');
        document.body.appendChild(element);
        element.href = window.URL.createObjectURL(file);
        element.download = apiUrl.includes('.xlsx')
          ? apiUrl.substring(apiUrl.lastIndexOf('/') + 1)
          : 'STAFF_FILE_UPLOAD_TEMPLATE.xlsx';
        element.click();
        this.loading.hide();
      });
  }

  handleCustomAction(evnt) {
    this.onCustomAction.emit(evnt);
  }

  hasPermission(permissionFor: 'add' | 'edit' | 'delete' | 'view') {
    if (
      !(
        this._tableConfig.permissions &&
        this._tableConfig.permissions[permissionFor] &&
        this._tableConfig.permissions[permissionFor].length
      )
    ) {
      return true;
    }

    const roles = this.keycloakService.getUserRoles(true);
    const rolesAllowed = this._tableConfig.permissions[permissionFor];

    return rolesAllowed.some((role) => roles.includes(role));
  }

  get actionColumnHidden() {
    return (
      this.tableConfig.actionColumnHidden ||
      !(
        this.hasPermission('view') ||
        this.hasPermission('edit') ||
        this.hasPermission('delete')
      )
    );
  }

  ngOnDestroy() {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  getFormGroup(formGroup: FormGroup) {
    this.onFormInit.emit(formGroup);
  }
}
